"use client";

import { useMemo } from "react";
import { toNumber } from "@/lib/calculators/truck-loading-merged/types";
import type { FitResult } from "@/lib/calculators/truck-loading-merged/types";

export function useOptimizedLoading(
  specificQtyEnabled: boolean,
  targetQty: string,
  result: FitResult,
  weightLbs: number,
) {
  const parsedTargetQty = Math.max(Math.round(toNumber(targetQty)), 0);

  return useMemo(() => {
    if (
      !specificQtyEnabled ||
      parsedTargetQty <= 0 ||
      result.floorCount <= 0 ||
      result.stackLayers <= 0
    ) {
      return { perFloor: result.floorCount, stackLayers: result.stackLayers };
    }

    const maxBySpace = result.floorCount * result.stackLayers;
    const hasWeightLimit = weightLbs > 0;
    const effectiveMax = hasWeightLimit
      ? Math.min(maxBySpace, result.totalByWeight)
      : maxBySpace;

    if (effectiveMax <= 0) {
      return { perFloor: result.floorCount, stackLayers: result.stackLayers };
    }

    const trucksNeeded = Math.ceil(parsedTargetQty / effectiveMax);
    const itemsPerTruck = Math.ceil(parsedTargetQty / trucksNeeded);

    if (itemsPerTruck <= result.stackLayers) {
      return { perFloor: 1, stackLayers: itemsPerTruck };
    }

    const optimalPerFloor = Math.min(
      Math.ceil(itemsPerTruck / result.stackLayers),
      result.floorCount,
    );
    return { perFloor: optimalPerFloor, stackLayers: result.stackLayers };
  }, [specificQtyEnabled, parsedTargetQty, result, weightLbs]);
}
